import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { MediaManagerTemplate } from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import { useDeleteMediaManagerTemplateMutation } from 'modules/manager/store/media-manager-api';
import { useCallback, useState } from 'react';
import { describeApiError, isDuplicateRequest } from 'shared/errors';
import Modal from 'ud-ui/components/modal';

/**
 * The group refetches by the template's tag — the group provides the tags of its templates (IMGM-1515).
 * A refusal keeps the window open with the reason in place of the description (IMGM-1549).
 */
function useDeleteTemplateModal() {
  const { t } = useLingui();
  // Кнопки окна запирает промис `onConfirm` — внешний `isLoading` не нужен (IMGM-1561).
  const [deleteTemplate] = useDeleteMediaManagerTemplateMutation();
  const [template, setTemplate] = useState<MediaManagerTemplate | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const open = useCallback((next: MediaManagerTemplate) => {
    setTemplate(next);
    setFailure(null);
    setIsOpen(true);
  }, []);
  const close = useCallback(() => {
    setIsOpen(false);
    setFailure(null);
  }, []);

  const onSubmit = useCallback(async () => {
    if (!template) return;
    try {
      await deleteTemplate(template.id).unwrap();
      close();
    } catch (error) {
      // Код 15 — повтор: первый запрос шаблон уже удалил, окно закрывается как при успехе.
      if (isDuplicateRequest(error)) {
        close();
        return;
      }
      setFailure(describeApiError(error, msg`Не удалось удалить шаблон`));
    }
  }, [template, deleteTemplate, close]);

  const templateName = template?.name;

  return {
    open,
    close,
    modal: (
      <Modal.Confirm
        show={isOpen}
        title={templateName ? t`Удалить шаблон «${templateName}»?` : t`Удалить шаблон?`}
        description={failure ?? t`Это действие необратимо.`}
        onConfirm={onSubmit}
        onCancel={close}
      />
    ),
  };
}

export default useDeleteTemplateModal;
